import React, { useState, useEffect } from "react";
import { db } from "@/api/db";
import { createPageUrl } from "@/utils";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle, Clock, Star } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export default function AdminProjectEvaluations() {
  const params = new URLSearchParams(window.location.search);
  const projectId = params.get("project_id");

  const [project, setProject] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [evaluations, setEvaluations] = useState({});
  const [loading, setLoading] = useState(true);
  const [evaluating, setEvaluating] = useState(null);
  const [evalForm, setEvalForm] = useState({ score: 70, feedback: "", strengths: "", improvements: "" });

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    const [projs, subs] = await Promise.all([
      db.entities.PracticalProject.filter({ id: projectId }),
      db.entities.ProjectSubmission.filter({ project_id: projectId }),
    ]);
    setProject(projs[0] || null);

    setSubmissions(subs);

    const evalMap = {};
    for (const sub of subs) {
      const evs = await db.entities.ProjectEvaluation.filter({ submission_id: sub.id });
      if (evs[0]) evalMap[sub.id] = evs[0];
    }
    setEvaluations(evalMap);
    setLoading(false);
  }

  async function handleEvaluate(sub) {
    const ev = {
      submission_id: sub.id,
      project_id: sub.project_id,
      candidate_id: sub.candidate_id,
      score: Number(evalForm.score),
      feedback: evalForm.feedback,
      strengths: evalForm.strengths.split("\n").map(s => s.trim()).filter(Boolean),
      improvements: evalForm.improvements.split("\n").map(s => s.trim()).filter(Boolean),
      points_earned: Math.round((Number(evalForm.score) / 100) * (project?.points || 100)),
      evaluated_by: "admin",
      evaluated_at: new Date().toISOString(),
    };
    await db.entities.ProjectEvaluation.create(ev);
    await db.entities.ProjectSubmission.update(sub.id, { status: "evaluated" });
    // Award points to user
    const profiles = await db.entities.UserProfile.filter({ candidate_id: sub.candidate_id }, "-created_at", 1);
    if (profiles[0]) {
      await db.entities.UserProfile.update(profiles[0].id, {
        portus_score: (profiles[0].portus_score || 0) + ev.points_earned,
      });
    }
    setEvaluating(null);
    await loadData();
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <Link to={createPageUrl("AdminPracticeLab")} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800">
        <ArrowLeft className="w-4 h-4" /> Voltar ao Practice Lab Admin
      </Link>

      <div>
        <h1 className="text-xl font-bold text-gray-900">Entregas — {project?.title}</h1>
        <p className="text-gray-500 text-sm mt-1">{submissions.length} entrega(s)</p>
      </div>

      {submissions.length === 0 && (
        <div className="text-center py-16 text-gray-400">Nenhuma entrega ainda.</div>
      )}

      {submissions.map(sub => {
        const ev = evaluations[sub.id];
        const isOpen = evaluating === sub.id;
        return (
          <Card key={sub.id} className="border-0 shadow-sm">
            <CardContent className="p-5 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-medium text-gray-900">{sub.candidate_name || sub.candidate_email || "Candidato"}</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Enviado em: {sub.submitted_at ? new Date(sub.submitted_at).toLocaleString("pt-BR") : "–"}
                  </p>
                </div>
                <Badge className={`border-0 text-xs ${ev ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                  {ev ? <><CheckCircle className="w-3 h-3 inline mr-1" />Avaliado ({ev.score}/100)</> : <><Clock className="w-3 h-3 inline mr-1" />Pendente</>}
                </Badge>
              </div>
              {sub.submission_url && (
                <a href={sub.submission_url} target="_blank" rel="noopener noreferrer" className="text-sm text-violet-600 hover:underline block">
                  🔗 Ver entregável
                </a>
              )}
              {sub.submission_text && (
                <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">{sub.submission_text}</p>
              )}

              {!ev && (
                <>
                  {!isOpen ? (
                    <Button
                      size="sm"
                      className="bg-violet-600 hover:bg-violet-700 gap-2"
                      onClick={() => { setEvaluating(sub.id); setEvalForm({ score: 70, feedback: "", strengths: "", improvements: "" }); }}
                    >
                      <Star className="w-4 h-4" /> Avaliar entrega
                    </Button>
                  ) : (
                    <div className="space-y-3 border-t pt-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-medium text-gray-700 block mb-1">Nota (0-100)</label>
                          <input type="number" min="0" max="100" value={evalForm.score} onChange={e => setEvalForm({ ...evalForm, score: e.target.value })}
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400" />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-700 block mb-1">Feedback geral</label>
                        <textarea rows={3} value={evalForm.feedback} onChange={e => setEvalForm({ ...evalForm, feedback: e.target.value })}
                          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 resize-none"
                          placeholder="Análise geral da entrega..." />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-700 block mb-1">Pontos fortes (um por linha)</label>
                        <textarea rows={2} value={evalForm.strengths} onChange={e => setEvalForm({ ...evalForm, strengths: e.target.value })}
                          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 resize-none" />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-700 block mb-1">A melhorar (um por linha)</label>
                        <textarea rows={2} value={evalForm.improvements} onChange={e => setEvalForm({ ...evalForm, improvements: e.target.value })}
                          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 resize-none" />
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" className="bg-violet-600 hover:bg-violet-700" onClick={() => handleEvaluate(sub)}>Salvar avaliação</Button>
                        <Button size="sm" variant="outline" onClick={() => setEvaluating(null)}>Cancelar</Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}